// Made-up price book for the price book round's stories (the approved prototype's world,
// pages/pricebook-concepts/data.js): Harbour Plumbing is invented, and so is every customer, address,
// price and count. Shaped as pricebook.listCategories and pricebook.listItems answer (prices in cents;
// cost only for those who see it), with the art each item's picture is drawn from. Storybook only.
import type { PictureSrc, PictureState } from '../components/Picture/Picture.tsx';
import { money } from '../format.ts';
import { ART, type ArtSubject } from '../stories/pricebook-art.ts';

export { ART, type ArtSubject };
export const COMPANY = 'Harbour Plumbing';
export type Viewer = 'owner' | 'office' | 'csr';
export const PEOPLE = {
  tech: { name: 'Riley Santos', short: 'Riley', role: 'Tech' },
  csr: { name: 'Jo Ames', short: 'Jo', role: 'CSR' },
  office: { name: 'Dana Pike', short: 'Dana', role: 'Office manager' },
  owner: { name: 'Nora Rowe', short: 'Nora', role: 'Owner' },
} as const;

export interface Category {
  id: string;
  name: string;
  parentId: string | null;
  art: ArtSubject | null;
}
export const CATEGORIES: Category[] = [
  { id: 'wh', name: 'Water heaters', parentId: null, art: 'tank-wh' },
  { id: 'wh-tank', name: 'Tank, replace', parentId: 'wh', art: 'tank-wh' },
  { id: 'wh-fix', name: 'Tank, repair', parentId: 'wh', art: 'flush' },
  { id: 'wh-tl', name: 'Tankless', parentId: 'wh', art: 'tankless' },
  { id: 'dr', name: 'Drains', parentId: null, art: 'drain' },
  { id: 'dr-clear', name: 'Clearing', parentId: 'dr', art: 'drain' },
  { id: 'dr-cam', name: 'Camera and locating', parentId: 'dr', art: 'camera' },
  { id: 'dr-sump', name: 'Sump pumps', parentId: 'dr', art: 'sump' },
  { id: 'fx', name: 'Fixtures', parentId: null, art: 'faucet' },
  { id: 'fx-kit', name: 'Kitchen faucets', parentId: 'fx', art: 'faucet' },
  { id: 'fx-bath', name: 'Bathroom faucets', parentId: 'fx', art: 'faucet' },
  { id: 'fx-toil', name: 'Toilets', parentId: 'fx', art: null },
  { id: 'wt', name: 'Water treatment', parentId: null, art: 'softener' },
  { id: 'wt-soft', name: 'Softeners', parentId: 'wt', art: 'softener' },
  { id: 'wt-filt', name: 'Water filters', parentId: 'wt', art: null },
  { id: 'vp', name: 'Valves and pressure', parentId: null, art: 'prv' },
  { id: 'vp-shut', name: 'Shut-offs', parentId: 'vp', art: 'shutoff' },
  { id: 'vp-prv', name: 'Pressure and expansion', parentId: 'vp', art: 'prv' },
  { id: 'vp-bf', name: 'Backflow', parentId: 'vp', art: 'backflow' },
  { id: 'ch', name: 'Charges and plans', parentId: null, art: 'plan' },
  { id: 'ch-fee', name: 'Trip and fees', parentId: 'ch', art: 'trip' },
  { id: 'ch-plan', name: 'Plans and warranties', parentId: 'ch', art: 'warranty' },
];

export type Kind = 'service' | 'material' | 'equipment' | 'discount';
export interface Words {
  name: string;
  what: string;
  included: string[];
  warranty: string | null;
  /** approved, or Jev's draft waiting for an OK */
  state: 'approved' | 'draft';
}
export interface Item {
  id: string;
  categoryId: string;
  code: string;
  type: Kind;
  name: string;
  /** null: not set yet */
  priceCents: number | null;
  memberPriceCents: number | null;
  costCents: number | null;
  art: ArtSubject | null;
  artState: PictureState;
  words: Words | null;
  sold12: number;
  lastSold: string;
  madeOf: { name: string; quantity: string; costCents: number }[];
  goes: { id: string; share: number }[];
  soldHours: number | null;
}

type Opt = Partial<Omit<Item, 'id' | 'categoryId' | 'code' | 'type' | 'name' | 'priceCents'>> & {
  member?: number;
  cost?: number;
};
const I = (
  id: string,
  categoryId: string,
  code: string,
  type: Kind,
  name: string,
  priceCents: number | null,
  o: Opt = {},
): Item => ({
  id,
  categoryId,
  code,
  type,
  name,
  priceCents,
  memberPriceCents:
    o.member ?? (priceCents == null ? null : type === 'service' ? Math.round(priceCents * 0.9) : priceCents),
  costCents: o.cost ?? null,
  art: o.art ?? null,
  artState: o.artState ?? (o.art ? 'ready' : 'none'),
  words: o.words ?? null,
  sold12: o.sold12 ?? 0,
  lastSold: o.lastSold ?? '21 Oct',
  madeOf: o.madeOf ?? [],
  goes: o.goes ?? [],
  soldHours: o.soldHours ?? null,
});
const W = (
  name: string,
  what: string,
  included: string[],
  warranty: string | null,
  state: Words['state'] = 'approved',
): Words => ({
  name,
  what,
  included,
  warranty,
  state,
});
const M = (name: string, quantity: string, costCents: number) => ({ name, quantity, costCents });
const G = (id: string, share: number) => ({ id, share });

export const ITEMS: Item[] = [
  I('g40', 'wh-tank', 'WH-G40', 'equipment', '40 gal gas water heater', 128000, {
    cost: 76000,
    art: 'tank-wh',
    sold12: 41,
    words: W(
      'A new 40-gallon gas water heater',
      'Enough hot water for one or two people.',
      ['The tank, its stand and drain pan', 'A 6-year tank warranty'],
      '6 years on the tank',
    ),
  }),
  I('g50', 'wh-tank', 'WH-G50', 'equipment', '50 gal gas water heater', 145000, {
    cost: 88000,
    art: 'tank-wh',
    sold12: 96,
    goes: [G('et2', 78), G('so34', 41), G('trip', 92), G('permit', 88)],
    words: W(
      'A new 50-gallon gas water heater',
      'Enough hot water for a family of four, on the venting you have.',
      ['The tank, its stand and drain pan', 'A 6-year tank warranty', 'Sized and set for your house'],
      '6 years on the tank',
    ),
  }),
  I('g50he', 'wh-tank', 'WH-G50HE', 'equipment', '50 gal high-efficiency gas', 198000, {
    cost: 133000,
    art: 'tank-wh',
    sold12: 22,
    words: W(
      'A high-efficiency 50-gallon gas water heater',
      'The same hot water on about a fifth less gas.',
      ['The tank, its stand and drain pan', 'A 10-year tank warranty'],
      '10 years on the tank',
    ),
  }),
  I('e50', 'wh-tank', 'WH-E50', 'equipment', '50 gal electric water heater', 119000, {
    cost: 70000,
    art: 'tank-wh',
    sold12: 18,
    words: W(
      'A new 50-gallon electric water heater',
      'Hot water with no gas line or venting.',
      ['The tank and its drain pan'],
      '6 years on the tank',
      'draft',
    ),
  }),
  I('whinst', 'wh-tank', 'WH-INST', 'service', 'Install, connect and test (tank)', 95000, {
    cost: 31000,
    art: 'tank-wh',
    sold12: 150,
    soldHours: 3,
    madeOf: [
      M('¾" flex gas connector, 18 in.', '1', 2400),
      M('¾" dielectric union', '2', 1100),
      M('Thread sealant, gas rated', '1', 600),
      M('Labour', '3 h', 22500),
    ],
    words: W(
      'Fitted, connected and tested',
      'We fit it on your gas and water lines and test every joint.',
      ['New connectors and unions', 'A gas leak test on every joint', 'Clean-up when we’re done'],
      '1 year on the labour',
    ),
  }),
  I('tc', 'wh-fix', 'WH-TC', 'service', 'Thermocouple and pilot, replaced', 18500, {
    cost: 6200,
    art: 'flush',
    sold12: 64,
    madeOf: [M('Thermocouple, universal', '1', 1800), M('Pilot assembly', '1', 2900)],
    words: W(
      'A new pilot and the sensor that keeps it lit',
      'For a pilot that lights, then goes out.',
      ['The pilot and thermocouple', 'Gas and venting checked'],
      '90 days on the repair',
    ),
  }),
  I('flush', 'wh-fix', 'WH-FL', 'service', 'Tank flush', 9900, {
    cost: 2100,
    art: 'flush',
    sold12: 210,
    words: W(
      'Your tank flushed of sediment',
      'Clears the grit that makes a tank rumble and wear out.',
      ['Drain, flush and refill', 'The relief valve tested'],
      '30 days',
    ),
  }),
  I('gv', 'wh-fix', 'WH-GV', 'service', 'Gas valve, replaced', 74000, {
    cost: 29000,
    sold12: 12,
    madeOf: [M('Gas control valve', '1', 21500)],
    words: W(
      'A new gas valve for your water heater',
      'For a tank whose gas valve has failed.',
      ['The valve and its fitting', 'A leak test'],
      '1 year on the valve',
      'draft',
    ),
  }),
  I('tl199', 'wh-tl', 'TL-199', 'equipment', 'Tankless heater, 199k BTU', 340000, {
    cost: 198000,
    art: 'tankless',
    sold12: 14,
    words: W(
      'A tankless heater that heats water as you use it',
      'Hot water that doesn’t run out, on the wall.',
      ['The heater and its wall bracket', 'A 15-year heat exchanger warranty'],
      '15 years on the heat exchanger',
    ),
  }),
  I('tlinst', 'wh-tl', 'TL-INST', 'service', 'Install, venting and gas line (tankless)', 165000, {
    cost: 52000,
    art: 'tankless',
    sold12: 14,
    soldHours: 7,
    words: W(
      'Hung on the wall, with new venting and a bigger gas line',
      'Everything a tankless heater needs to run safely.',
      ['New venting', 'A bigger gas line', 'A gas leak test'],
      '2 years on the labour',
    ),
  }),
  I('desc', 'wh-tl', 'TL-DES', 'service', 'Descale a tankless heater', 18900, {
    cost: 3400,
    art: 'flush',
    sold12: 31,
    words: W(
      'Your tankless heater descaled',
      'Keeps it heating at full strength in hard water.',
      ['Flush with descaler', 'Filter cleaned'],
      '30 days',
    ),
  }),
  I('main', 'dr-clear', 'DR-MAIN', 'service', 'Main line cleared, up to 100 ft', 39500, {
    cost: 6500,
    art: 'drain',
    sold12: 188,
    goes: [G('cam', 46)],
    words: W(
      'Your main drain cleared',
      'For backups in more than one drain at once.',
      ['Cleared up to 100 ft', 'Tested with running water'],
      '30 days',
    ),
  }),
  I('ks', 'dr-clear', 'DR-KS', 'service', 'Kitchen sink drain cleared', 22500, {
    cost: 3500,
    art: 'drain',
    sold12: 142,
    goes: [G('cam', 21)],
    words: W(
      'Your kitchen drain cleared',
      'For a sink that drains slowly or not at all.',
      ['Cleared to the main line', 'Tested with running water'],
      '30 days',
    ),
  }),
  I('cam', 'dr-cam', 'CAM-INSP', 'service', 'Camera inspection, with recording', 34900, {
    cost: 4200,
    art: 'camera',
    sold12: 97,
    words: W(
      'A camera look inside your drain, recorded for you',
      'Shows what’s causing the trouble before anything is dug up.',
      ['The inspection and a recording', 'Where the trouble is, marked'],
      'None needed',
    ),
  }),
  I('loc', 'dr-cam', 'CAM-LOC', 'service', 'Locate and mark the line', 17500, {
    cost: 2200,
    art: 'camera',
    sold12: 33,
  }),
  I('sump', 'dr-sump', 'SP-REP', 'service', 'Sump pump, replaced', 89000, {
    cost: 34000,
    art: 'sump',
    sold12: 26,
    madeOf: [M('Sump pump, ½ hp', '1', 21000), M('Check valve', '1', 3800)],
    words: W(
      'A new sump pump',
      'Keeps water out of your basement.',
      ['The pump and a new check valve', 'Tested with water'],
      '3 years on the pump',
    ),
  }),
  I('alarm', 'dr-sump', 'SP-ALM', 'service', 'High-water alarm, fitted', 14500, {
    cost: 3900,
    sold12: 9,
    artState: 'making',
    words: W(
      'An alarm that sounds before water rises',
      'A warning if the pump ever stops.',
      ['The alarm, fitted and tested'],
      '1 year',
    ),
  }),
  I('kfg', 'fx-kit', 'FX-KF-G', 'service', 'Kitchen faucet, standard, supplied and fitted', 38500, {
    cost: 14000,
    art: 'faucet',
    sold12: 58,
    madeOf: [M('Kitchen faucet, single lever', '1', 9800), M('Braided supply lines, pair', '1', 1600)],
    goes: [G('so12', 64), G('ks', 18)],
    words: W(
      'A new kitchen faucet, fitted',
      'A sturdy single-lever faucet.',
      ['The faucet and new supply lines', 'The old one taken away'],
      '1 year on the faucet',
    ),
  }),
  I('kfb', 'fx-kit', 'FX-KF-B', 'service', 'Kitchen faucet, pull-down, supplied and fitted', 54500, {
    cost: 21000,
    art: 'faucet',
    sold12: 71,
    madeOf: [M('Kitchen faucet, pull-down', '1', 16500), M('Braided supply lines, pair', '1', 1600)],
    goes: [G('so12', 64), G('ks', 18), G('cam', 9)],
    words: W(
      'A pull-down kitchen faucet, fitted',
      'A spray head that pulls down to rinse the whole sink.',
      ['The faucet and new supply lines', 'The old one taken away'],
      '5 years on the faucet',
    ),
  }),
  I('kfbb', 'fx-kit', 'FX-KF-T', 'service', 'Kitchen faucet, touch-free, supplied and fitted', 79500, {
    cost: 33000,
    art: 'faucet',
    sold12: 19,
    madeOf: [M('Kitchen faucet, touch-free', '1', 28000), M('Braided supply lines, pair', '1', 1600)],
    goes: [G('so12', 64)],
    words: W(
      'A touch-free kitchen faucet, fitted',
      'Wave a hand to turn it on; no greasy handle.',
      ['The faucet, its sensor and new supply lines', 'The old one taken away'],
      'Lifetime on the faucet, 1 year on the sensor',
    ),
  }),
  I('kfown', 'fx-kit', 'FX-KF-FIT', 'service', 'Fit the customer’s own kitchen faucet', 21000, {
    cost: 5200,
    art: 'faucet',
    sold12: 23,
    words: W(
      'Your faucet, fitted',
      'We fit the faucet you bought.',
      ['New supply lines', 'The old one taken away'],
      '1 year on the labour',
      'draft',
    ),
  }),
  I('bf', 'fx-bath', 'FX-BF', 'service', 'Bathroom faucet, supplied and fitted', 36500, {
    cost: 13000,
    art: 'faucet',
    sold12: 44,
    words: W(
      'A new bathroom faucet, fitted',
      'A two-handle or single-lever faucet for your basin.',
      ['The faucet and new supply lines'],
      '1 year on the faucet',
    ),
  }),
  I('toil', 'fx-toil', 'FX-TL', 'service', 'Toilet, supplied and fitted', 69000, {
    cost: 28000,
    sold12: 37,
    art: 'flush',
    artState: 'to_approve',
    words: W(
      'A new toilet, fitted',
      'A water-saving toilet with a new seal.',
      ['The toilet, seat and wax seal', 'The old one taken away'],
      '5 years on the toilet',
    ),
  }),
  I('soft', 'wt-soft', 'WT-S32', 'equipment', 'Softener, 32,000 grain, installed', 189000, {
    cost: 92000,
    art: 'softener',
    sold12: 17,
    words: W(
      'A water softener for the whole house',
      'Ends the white crust on taps and helps a heater last.',
      ['The softener and its bypass', 'Set for your water'],
      '10 years on the tank',
    ),
  }),
  I('so34', 'vp-shut', 'VP-SO34', 'service', 'Quarter-turn shut-off, ¾", fitted', 16500, {
    cost: 2800,
    art: 'shutoff',
    sold12: 120,
    words: W(
      'A quarter-turn shut-off valve',
      'Turns the water off in one quick turn.',
      ['The valve, fitted and tested'],
      '1 year',
    ),
  }),
  I('so12', 'vp-shut', 'VP-SO12', 'service', 'Angle stops under a sink, pair', 18500, {
    cost: 2600,
    art: 'shutoff',
    sold12: 85,
    words: W(
      'New shut-offs under your sink',
      'So the sink can be shut off without the whole house.',
      ['Two valves, fitted and tested'],
      '1 year',
    ),
  }),
  I('prv', 'vp-prv', 'VP-PRV', 'equipment', 'Pressure-reducing valve, house', 49500, {
    cost: 17500,
    art: 'prv',
    sold12: 39,
    words: W(
      'A pressure-reducing valve for the house',
      'Keeps high street pressure from straining pipes and fixtures.',
      ['The valve, set and tested'],
      '5 years',
    ),
  }),
  I('et2', 'vp-prv', 'VP-ET2', 'equipment', 'Expansion tank, 2 gal', 16500, {
    cost: 5400,
    art: 'exp-tank',
    sold12: 102,
    words: W(
      'An expansion tank',
      'So pressure never strains the new tank.',
      ['The tank, fitted and charged'],
      '5 years',
    ),
  }),
  I('bflow', 'vp-bf', 'VP-BF', 'equipment', 'Backflow preventer, fitted and tested', 42000, {
    cost: 15800,
    art: 'backflow',
    sold12: 11,
    words: W(
      'A backflow preventer',
      'Keeps used water from flowing back into the city’s.',
      ['The device, fitted', 'Its first test and report'],
      '1 year',
    ),
  }),
  I('bft', 'vp-bf', 'VP-BFT', 'service', 'Backflow test and report', 9500, {
    cost: 1800,
    art: 'backflow',
    sold12: 0,
    lastSold: '14 Jun 2025',
  }),
  I('trip', 'ch-fee', 'CH-HAUL', 'service', 'Haul away the old tank', 9500, {
    member: 9500,
    cost: 2500,
    art: 'trip',
    sold12: 160,
    words: W(
      'We take the old tank away',
      'Drained, carried out and recycled.',
      ['Disposal fees included'],
      null,
    ),
  }),
  I('permit', 'ch-fee', 'CH-PERMIT', 'service', 'Gas permit', 12000, {
    member: 12000,
    cost: 8500,
    art: 'permit',
    sold12: 148,
    words: W(
      'The gas permit, filed for you',
      'We file it and book the inspection.',
      ['The permit and its inspection'],
      null,
    ),
  }),
  I('diag', 'ch-fee', 'CH-DIAG', 'service', 'Diagnostic visit', 12900, {
    member: 12900,
    art: 'trip',
    sold12: 610,
    words: W(
      'A visit to find the problem',
      'A plumber comes and tells you what’s wrong and the ways to fix it.',
      ['Up to 30 minutes on site'],
      null,
    ),
  }),
  I('after', 'ch-fee', 'CH-AFTER', 'service', 'After-hours charge', null, {
    art: 'trip',
    sold12: 0,
    lastSold: 'never',
  }),
  I('club', 'ch-plan', 'PL-CC', 'service', 'Comfort Club, a year', 21900, {
    member: 21900,
    art: 'plan',
    sold12: 77,
    words: W(
      'Comfort Club, for a year',
      'A yearly tune-up and 10 % off labour.',
      ['One tune-up a year', '10 % off labour', 'No trip charge'],
      null,
    ),
  }),
  I('war5', 'ch-plan', 'WR-LAB5', 'service', '5-year labour warranty', 24900, {
    member: 24900,
    art: 'warranty',
    sold12: 34,
    words: W(
      'Five years on our labour',
      'If our work fails, we come back at no charge.',
      ['Parts we fitted, re-fitted'],
      '5 years',
    ),
  }),
];
export const BY_ID: Record<string, Item> = Object.fromEntries(ITEMS.map((i) => [i.id, i]));
export const CAT_BY_ID: Record<string, Category> = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));
export const childrenOf = (id: string | null) => CATEGORIES.filter((c) => c.parentId === id);
/** The items in a system or a task (a system holds its tasks' items). */
export const itemsIn = (id: string | null) =>
  id == null ? ITEMS : ITEMS.filter((i) => i.categoryId === id || CAT_BY_ID[i.categoryId]?.parentId === id);
/** "Water heaters › Tank, replace" */
export const whereOf = (i: Item) => {
  const t = CAT_BY_ID[i.categoryId]!;
  const s = t.parentId ? CAT_BY_ID[t.parentId] : null;
  return s ? `${s.name} › ${t.name}` : t.name;
};
export const pictureOf = (i: Item): PictureSrc | null => (i.art ? ART[i.art] : null);
export const price = (c: number | null) => (c == null ? 'Price not set' : money(c));
/** Whole dollars, as a line in a column shows them: "$1,450" */
export const dollars = (c: number) => `$${Math.round(c / 100).toLocaleString('en-CA')}`;

// health: what a book needs, as filters (no picture, no customer words, price not set, unused for a year),
// and what techs asked for that isn't in the book
export type Health = 'all' | 'nopic' | 'nowords' | 'noprice' | 'unused' | 'asks';
export const HEALTH: Record<
  Exclude<Health, 'all' | 'asks'>,
  { label: string; test: (i: Item) => boolean }
> = {
  nopic: { label: 'No picture', test: (i) => i.artState !== 'ready' },
  nowords: { label: 'No customer words', test: (i) => !i.words || i.words.state !== 'approved' },
  noprice: { label: 'Price not set', test: (i) => i.priceCents == null },
  unused: { label: 'Unused for a year', test: (i) => i.sold12 === 0 },
};
/** What the whole (made-up) book holds, for the counts a real book shows. */
export const BOOK = { items: 1046 };

export interface Ask {
  id: string;
  what: string;
  who: string;
  job: string;
  when: string;
  state: 'Asked' | 'Being shaped' | 'Waiting for an OK';
  times: number;
}
export const ASKS: Ask[] = [
  {
    id: 'd1',
    what: 'Flapper valve for an old two-piece toilet',
    who: 'Riley S.',
    job: 'J-24603 · Okafor family',
    when: 'Today, 7:58 a.m.',
    state: 'Asked',
    times: 2,
  },
  {
    id: 'd2',
    what: 'Hose bib, frost-free, 12 in.',
    who: 'Sam K.',
    job: 'J-24581 · Moreno residence',
    when: 'Yesterday',
    state: 'Being shaped',
    times: 1,
  },
  {
    id: 'd3',
    what: 'Recirculating pump on a tank heater',
    who: 'Riley S.',
    job: 'J-24577 · Lindqvist residence',
    when: '22 Oct',
    state: 'Waiting for an OK',
    times: 3,
  },
];

/** pricebook.itemHistory for the item the sheet opens (made up). */
export const HISTORY = [
  {
    id: 'h1',
    when: '2 Oct 2026, 9:52 a.m.',
    who: 'Nora R.',
    what: 'Price $1,420.00 to $1,450.00',
    why: 'Supplier price up 4 %',
  },
  {
    id: 'h2',
    when: '18 Sep 2026, 4:10 p.m.',
    who: 'Jev, approved by Dana P.',
    what: 'Customer words written',
    why: 'Batch 3 of the customer words',
  },
  {
    id: 'h3',
    when: '24 Aug 2026, 11:03 a.m.',
    who: 'Imported',
    what: 'Item created in Water heaters › Tank, replace',
    why: '',
  },
];

// Priya's kitchen faucet, the CSR's estimate on a call: Start from's sets for the job type, the sold
// pattern for jobs like this, this address (no quotes before), then a blank option
export interface StartSet {
  id: string;
  name: string;
  note: string;
  pictures: ArtSubject[];
  options: { letter: string; tier: string; title: string; lines: string[] }[];
}
export const PRIYA = {
  job: 'Job 24655',
  number: 'Estimate 3140',
  name: 'Kitchen faucet',
  customer: 'Priya Nair',
  why: 'kitchen faucet drips; the sink drains slowly',
  member: false,
};
export const START_SETS: StartSet[] = [
  {
    id: 'set-kf',
    name: 'Kitchen faucet, three ways',
    note: 'Most used for this job type · 3 options: standard, pull-down, touch-free',
    pictures: ['faucet', 'faucet', 'faucet'],
    options: [
      { letter: 'A', tier: 'Good', title: 'A standard faucet', lines: ['kfg'] },
      { letter: 'B', tier: 'Better', title: 'A pull-down faucet', lines: ['kfb', 'so12'] },
      { letter: 'C', tier: 'Best', title: 'A touch-free faucet', lines: ['kfbb', 'so12'] },
    ],
  },
  {
    id: 'pat-kf',
    name: 'Jobs like this',
    note: 'Sold 41 times this year: a pull-down faucet and new angle stops. Most came to $690–$780.',
    pictures: ['faucet', 'shutoff'],
    options: [{ letter: 'A', tier: 'Good', title: 'A pull-down faucet', lines: ['kfb', 'so12'] }],
  },
  {
    id: 'set-dr',
    name: 'Slow drain',
    note: '3 options: clear it, clear and camera, main line',
    pictures: ['drain', 'camera', 'drain'],
    options: [
      { letter: 'A', tier: 'Good', title: 'Clear the sink drain', lines: ['ks'] },
      { letter: 'B', tier: 'Better', title: 'Clear it and look', lines: ['ks', 'cam'] },
      { letter: 'C', tier: 'Best', title: 'Clear the main line', lines: ['main', 'cam'] },
    ],
  },
];
